// bridge — the two pure mappings between a `Route` and the frame, reusing the
// existing focus projection + existing `FrameTransition`s (no new fold arm).
//
//   routeOfFrame(frame): Route | null      — state → route (the push source)
//   routeToFrame(route): FrameTransition[]  — route → transitions (the pop sink)
//
// Both are PURE. `routeToFrame` handles every arm whose transition needs no
// shell state (Journal / Library / Settings / Conversation — an id or an enum
// is all they carry). The arms that need shell-only knowledge return `[]` here
// and are completed by the impure wiring (useUrlNavigation), which resolves them
// with the state a pure function can't reach:
//   - `chat`: "go to a fresh/nascent conversation" needs the open-conversation
//     set + a minted id (ids never in a pure fold) → the wiring's `chatNav`.
//   - `package`: `open-resource` needs a `ResourceRef` with
//     a TITLE the URL can't carry → the wiring resolves id→ref off the loaded
//     Library (`refFor`), falling back to a title-less placeholder that
//     `reconcile-tabs` heals on load.
//   - `quality`: `open-quality-report` needs the material's title → same
//     `refFor` resolution in the wiring.
//   - `notFound`: no frame transition at all → the wiring raises the 404 gate.
// This keeps `routeToFrame` pure + total; the wiring overlays only the arms
// that genuinely need state (coordinator's instruction: state in the wiring,
// never in the pure bridge).
//
// routeOfFrame is built on `focusedViewOf` (the same "what is the window focused
// on" projection the history recorder consumes). Two reads it layers on top:
//   - the Library facet is re-hydrated off the focused Search tab
//     (`tabSetOfPaneTree(...).active.kind`), now that the paneTree threads the
//     seed facet on the Search member (F48) — so opening the library filtered
//     mirrors to `?filter=<facet>`;
//   - a full-quality report is a pass-through in focus history (`focusedViewOf`
//     returns null for it), so it is recovered directly off the active tab.
// The nascent-vs-durable split for a conversation needs the durability signal
// (shell state): this returns `conversation{id}` for any focused conversation,
// and `mirroredRoute` (below) applies the split with the durability the wiring
// passes in.
//
// Discipline (dev-ts): pure — no I/O; total switches ending in
// `exhaustSwitchCase`; SAME empty array is fine (callers only iterate).

import { exhaustSwitchCase } from "@bilda/utils";
import { tabSetOfPaneTree } from "../../components/Navigation/paneTree.js";
import type { Durability } from "../conversationSummary.js";
import { focusedViewOf } from "../frame/fold.js";
import type { FrameState, FrameTransition } from "../frame.js";
import { type Route, routeNewChat } from "./types.js";

// state → route. `null` = the focused view is not URL-addressable (there is no
// such case today, but the total signature keeps the push layer honest).
export const routeOfFrame = (frame: FrameState): Route | null => {
    const active = tabSetOfPaneTree(frame.paneTree).active;
    const view = focusedViewOf(frame.paneTree);
    if (view === null) {
        // A full-quality report is pass-through in focus history — recover it
        // off the active tab so opening one still mirrors to `/quality/:id`.
        return active.type === "quality"
            ? { type: "quality", materialId: active.materialId }
            : null;
    }
    switch (view.type) {
        case "home":
            return { type: "journal" };
        case "studio":
            return frame.studioProject === null
                ? { type: "studio" }
                : { type: "studio", projectId: frame.studioProject };
        case "projects":
            return { type: "projects" };
        case "knowledge":
            // The source gone into (`/knowledge/{id}`), else the list.
            return {
                type: "knowledge",
                selected:
                    active.type === "knowledge" ? active.selectedId : undefined,
            };
        case "settings":
            return { type: "settings", section: frame.settingsSection };
        case "search": {
            // The live query text once typed (it owns the kind from then on:
            // a tab writes `type:` into it), else the seed facet (F48).
            const search = active.type === "search" ? active : undefined;
            return {
                type: "library",
                query: search?.query ?? "",
                facet: search?.query === undefined ? search?.kind : undefined,
                // B4 — the opened row (`/library/{id}`).
                selected: search?.selectedId,
            };
        }
        case "conversation":
            // Any focused conversation → `conversation{id}`; the wiring
            // downgrades a NASCENT one to `chat` (`/`) using the durability
            // signal a pure function can't read.
            return { type: "conversation", conversationId: view.id };
        case "resource":
            // Map the ref TYPE to the human URL kind (package ⇄ export), by
            // exhaustive switch — never a cast.
            switch (view.ref.type) {
                // A source's (and a raw file's) home is the Knowledge bank.
                case "source":
                case "file":
                    return { type: "knowledge", selected: view.ref.id };
                // A material's home is its library detail (edited in place).
                case "material":
                    return {
                        type: "library",
                        query: "",
                        facet: "material",
                        selected: view.ref.id,
                    };
                case "export":
                    return { type: "package", id: view.ref.id };
                default:
                    return exhaustSwitchCase(view.ref);
            }
        case "landing":
            // The legacy hero view — a nascent compose, i.e. the new chat.
            return { type: "chat" };
        default:
            return exhaustSwitchCase(view);
    }
};

// The route the bar should show for the frame's derived route, given the focused
// conversation's durability: a NASCENT conversation is the new chat (`/`), a
// DURABLE one is `/c/:id`, and while durability is UNKNOWN (the conversation
// list not held yet, or failed) there is nothing to write: `null` keeps the bar
// as it is. D50: reading "unknown" as nascent rewrote a reloaded `/c/:id` to `/`
// while the list loaded, so a second reload (or a list that never loaded) landed
// on a new chat.
export const mirroredRoute = (
    derived: Route | null,
    durability: (conversationId: string) => Durability,
): Route | null => {
    if (derived === null || derived.type !== "conversation") return derived;
    const known = durability(derived.conversationId);
    switch (known) {
        case "durable":
            return derived;
        case "nascent":
            return routeNewChat;
        case "unknown":
            return null;
        default:
            return exhaustSwitchCase(known);
    }
};

// route → transitions (the pure arms). See the header for the arms deferred to
// the wiring (chat / resources / quality / notFound) — each returns `[]` here.
export const routeToFrame = (route: Route): readonly FrameTransition[] => {
    switch (route.type) {
        case "journal":
            return [{ type: "open-home" }];
        case "studio":
            return [
                route.projectId === undefined
                    ? { type: "open-studio" }
                    : { type: "open-studio", projectId: route.projectId },
            ];
        case "projects":
            return [{ type: "open-projects" }];
        case "knowledge":
            return [{ type: "open-knowledge", selectedId: route.selected }];
        case "library":
            return [
                {
                    type: "open-search",
                    kind: route.facet,
                    selectedId: route.selected,
                    query: route.query === "" ? undefined : route.query,
                },
            ];
        case "settings":
            return [{ type: "open-settings", section: route.section }];
        case "conversation":
            return [
                {
                    type: "enter-full-chat",
                    conversationId: route.conversationId,
                },
            ];
        // Completed by the wiring (need shell state — see the header):
        case "chat":
        case "package":
        case "quality":
        case "notFound":
            return [];
        default:
            return exhaustSwitchCase(route);
    }
};

// How a frame-driven URL change enters the browser history. The library is
// one level deep: going INTO an item or back OUT to the list is a step Back
// undoes (push); everything else inside the library replaces — typing a
// query, picking a tab, stepping prev/next between items — so Back from an
// item always lands on the list in one step, never on every keystroke or
// every sibling visited. Any other move between destinations pushes.
export type HistoryMode = "push" | "replace";
export const historyModeOf = (from: Route, to: Route): HistoryMode =>
    ((from.type === "library" && to.type === "library") ||
        (from.type === "knowledge" && to.type === "knowledge")) &&
    (from.selected === undefined) === (to.selected === undefined)
        ? "replace"
        : "push";
